import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { SCAN_TYPE } from 'src/services/consts/shared.const'
import { isActiveScanStatus } from 'src/services/helpers/scan/status.helper'
import { scanStatusToHistoryItem } from 'src/services/helpers/scans/history.helper'

function findRowIndex(rows, scan) {
  if (scan.scanId) {
    const byId = rows.findIndex((row) => row.scanId === scan.scanId)
    if (byId !== -1) return byId
    return rows.findIndex((row) => !row.scanId && isActiveScanStatus(row.status))
  }
  return rows.findIndex((row) => isActiveScanStatus(row.status))
}

function mergeRow(existing, item) {
  return {
    ...existing,
    ...item,
    startedAt: item.startedAt || existing.startedAt,
    completedAt: item.completedAt || existing.completedAt,
  }
}

/** Merge latest scan status into a history list (pure — safe in queryFn and setQueryData). */
export function mergeScanIntoHistory(rows, scan) {
  const item = scanStatusToHistoryItem(scan)
  if (!item) return rows

  const index = findRowIndex(rows, scan)
  if (index === -1) {
    return isActiveScanStatus(scan.status) ? [item, ...rows] : rows
  }

  const next = rows.map((row, i) => (i === index ? mergeRow(row, item) : row))

  if (scan.scanId) {
    return next.filter((row, i) => i === index || !(!row.scanId && isActiveScanStatus(row.status)))
  }

  return next
}

function mergeScanIntoDetail(existing, scan) {
  const base = existing && typeof existing === 'object' ? existing : {}
  return {
    ...mergeRow(base, scan),
    results: scan.results ?? base.results,
  }
}

/** Merge latest scan status into `[SCAN_DETAIL, siteId, scanId]` when that query is mounted. */
function syncScanDetailFromStatus(queryClient, siteId, scan) {
  if (!siteId || !scan?.status || !scan.scanId) return

  const detailKey = [QUERY_KEYS.SCAN_DETAIL, siteId, scan.scanId]
  if (queryClient.getQueryData(detailKey) !== undefined) {
    queryClient.setQueryData(detailKey, (existing) => mergeScanIntoDetail(existing, scan))
  }
}

/** Upsert latest scan into mounted `[SCAN_HISTORY, siteId]` and `[SCAN_DETAIL, siteId, scanId]` caches. */
export function syncScanHistoryFromStatus(queryClient, siteId, scan) {
  if (!siteId || !scan?.status) return

  if (queryClient.getQueryData([QUERY_KEYS.SCAN_HISTORY, siteId]) !== undefined) {
    queryClient.setQueryData([QUERY_KEYS.SCAN_HISTORY, siteId], (list) =>
      mergeScanIntoHistory(list ?? [], scan)
    )
  }

  syncScanDetailFromStatus(queryClient, siteId, scan)
}

/**
 * The optimistic merge above only ever patches the history row matching the
 * *current* latest scanId. Once the backend auto-chains a full scan, `/scans/latest`
 * moves on to the full scan's scanId and the preview's own row is never revisited —
 * it's stuck showing whatever status was last merged in while it was still active,
 * because the 15s poll interval almost always misses the brief window where latest
 * still reports the preview as completed before the full scan's record lands.
 *
 * Detect that handoff (previous scanType preview → new scanType full, different
 * scanId) and force one real history refetch so the preview's row picks up its true
 * terminal status from the server, instead of staying frozen mid-scan forever.
 *
 * @param {import('@tanstack/vue-query').QueryClient} queryClient
 * @param {string} siteId
 * @param {import('src/services/helpers/scan/status.helper').ScanStatus|null|undefined} previousScan
 * @param {import('src/services/helpers/scan/status.helper').ScanStatus} scan
 */
export function refetchHistoryOnScanTypeHandoff(queryClient, siteId, previousScan, scan) {
  const handedOff =
    previousScan?.scanType === SCAN_TYPE.PREVIEW &&
    scan?.scanType === SCAN_TYPE.FULL &&
    previousScan.scanId !== scan.scanId
  if (!siteId || !handedOff) return

  const historyKey = [QUERY_KEYS.SCAN_HISTORY, siteId]
  if (queryClient.getQueryData(historyKey) === undefined) return
  queryClient.refetchQueries({ queryKey: historyKey, exact: true }).catch(() => {})
}
